import React from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { KRAJ } from '../../kraj'
import StronaDokumentu, { NaglowekSekcji } from './StronaDokumentu'
import SekcjaProdukcji from './SekcjaProdukcji'

/**
 * Strona „Prognozowana produkcja energii" — czwarta strona dokumentu.
 *
 * PO CO OSOBNA STRONA: prognoza była dotąd wyłącznie zakładką na ekranie i NIE
 * trafiała do PDF-a. Klient dostawał ofertę bez jedynej liczby, która mówi, co
 * ta instalacja dla niego zrobi — ile prądu wyprodukuje.
 *
 * Sama prognoza i wykres zostają w `SekcjaProdukcji`, bo to ten sam widok co na
 * ekranie i nie ma powodu utrzymywać dwóch. Ta strona dokłada wyłącznie ramę
 * dokumentu i dwa kafelki, które mają zostać w głowie po odłożeniu kartki.
 */

const StronaProdukcji = ({
  dane, onZmiana, kwp, pojemnoscMagazynuKwh, prognoza,
  nazwaFirmy, numer = 4, zeStron = 5, podtytul = '',
  // Zmiana odpowiedzi przy kliencie wraca do jego karty (`ModalSchematu`); `zrodloProdukcji`: tekst skąd produkcja
  // („Projekt 3D, z uwzględnieniem cienia”), drukowany na stronie; `produkcjaZ3D`: bez pytań, które nic nie zmieniają.
  onEdycja = null, zrodloProdukcji = '', produkcjaZ3D = false,
}) => {
  const { t } = useTranslation('projekt')
  const roczna = prognoza?.rocznie?.produkcjaKwh
  const uzysk = prognoza?.uzyskJednostkowy

  return (
    <StronaDokumentu numer={numer} zeStron={zeStron} nazwaFirmy={nazwaFirmy} podtytul={podtytul}>
      <NaglowekSekcji
        tytul={t('produkcja.strona_tytul')}
        opis={uzysk
          ? t('produkcja.strona_opis', { uzysk: Math.round(uzysk) })
          : undefined}
      />

      {roczna != null && (
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          <div className="rounded-2xl px-5 py-5"
               style={{ backgroundColor: 'var(--vv-dok)', color: 'var(--vv-dok-na-pasku)' }}>
            <p className="text-[11px] uppercase tracking-wide opacity-60 font-semibold">
              {t('produkcja.produkcja_roczna')}
            </p>
            <p className="mt-1.5 text-3xl font-extrabold tabular-nums leading-none">
              {Math.round(roczna).toLocaleString(KRAJ.locale)} kWh
            </p>
            <p className="mt-2 text-xs opacity-60">
              {t('produkcja.pierwszy_rok')}
            </p>
          </div>

          {uzysk != null && (
            <div className="rounded-2xl px-5 py-5 border"
                 style={{ borderColor: 'var(--vv-dok-obwodka)', backgroundColor: 'var(--vv-dok-jasny)' }}>
              <p className="text-[11px] uppercase tracking-wide font-semibold"
                 style={{ color: 'var(--vv-dok-ciemny)' }}>
                {t('produkcja.uzysk_jednostkowy')}
              </p>
              <p className="mt-1.5 text-3xl font-extrabold tabular-nums leading-none"
                 style={{ color: 'var(--vv-dok-ciemny)' }}>
                {Math.round(uzysk)} kWh / kWp
              </p>
              <p className="mt-2 text-xs text-gray-500">
                {t('produkcja.typowa_wartosc')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Skąd produkcja, na ekranie i na wydruku: ta sama liczba z Projektu 3D i z tabeli województwa to dwie
          różne rzeczy, a klient ma wiedzieć, którą dostał. */}
      {zrodloProdukcji && (
        <p className="mb-4 text-xs text-gray-600">
          <span className="font-semibold text-gray-800">Produkcja:</span> {zrodloProdukcji}
        </p>
      )}

      <SekcjaProdukcji
        dane={dane} onZmiana={onZmiana} onEdycja={onEdycja} produkcjaZ3D={produkcjaZ3D}
        kwp={kwp} pojemnoscMagazynuKwh={pojemnoscMagazynuKwh} prognoza={prognoza}
        naWydruk bezNaglowka
      />

      {/* Zachęta do podania zużycia. Pokazujemy ją TYLKO gdy zużycia naprawdę nie
          ma — po jego podaniu autokonsumpcja jest już policzona i to zdanie
          mówiłoby klientowi, że czegoś brakuje, choć nie brakuje. */}
      {!Number(dane?.zuzycieRoczne) && (
        <div className="mt-5 flex items-start gap-2.5 rounded-xl border p-3.5"
             style={{ borderColor: 'var(--vv-dok-obwodka)', backgroundColor: 'var(--vv-dok-jasny)' }}>
          <InformationCircleIcon className="w-5 h-5 flex-shrink-0"
                                 style={{ color: 'var(--vv-dok-ciemny)' }} />
          <p className="text-xs text-gray-600 leading-5">
            {t('produkcja.zacheta_zuzycie')}
          </p>
        </div>
      )}
    </StronaDokumentu>
  )
}

export default StronaProdukcji
